import React, { useState } from 'react';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Tooltip,
} from 'reactstrap';
import './TemplateBlocks.scss';
import classNames from 'classnames';

type TemplateBlockProps = {
  isShared?: boolean;
  isNew?: boolean;
  title: string;
  details?: string;
  subTitle?: string;
  img?: string;
  id?: number;
  searchToken?: string;
  icon?: React.ReactNode;
  onClick?: (id?: number, searchToken?: string) => any;
  onDelete?: (id?: number) => any;
  onGetCode?: (id?: number, searchToken?: string) => any;
  onEdit?: (id?: number) => any;
};

const TemplateBlock = ({
  id,
  isShared,
  isNew,
  details,
  subTitle,
  onDelete,
  onClick,
  onEdit,
  onGetCode,
  img,
  title,
  icon,
  searchToken,
}: TemplateBlockProps) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const onBlockClick = () => onClick && onClick(id, searchToken);
  const onDeleteClick = () => onDelete && onDelete(id);
  const onGetCodeClick = () => onGetCode && onGetCode(id, searchToken);
  const onEditClick = () => onEdit && onEdit(id);

  const onDropdownToggle = (e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDropdownOpen(!isDropdownOpen);
  };

  const onTooltipToggle = () => setIsTooltipOpen(!isTooltipOpen);

  const containerClassName = classNames('template-block', {
    'template-block--new': isNew,
  });

  const iconClassName = classNames('template-block__icon');

  const contentClassName = classNames('template-block__content', {
    'template-block__content--new': isNew,
  });

  const titleClassName = classNames('template-block__title', {
    'template-block__title--new': isNew,
  });

  return (
    <div className={containerClassName} onClick={onBlockClick}>
      <div className={`template-block__bg-wr template-block__bg-wr_${!!isNew}`}>
        {img && (
          <div
            style={{ backgroundImage: `url(${img}` }}
            className="template-block__bg"
          />
        )}
        {isNew && <div className="template-block__bg-overlay" />}
        {icon && <div className={iconClassName}>{icon}</div>}
      </div>
      {!isNew && (
        <div className="template-block__dropdown">
          <Dropdown isOpen={isDropdownOpen} toggle={onDropdownToggle}>
            <DropdownToggle caret>
              <i className="fa fa-ellipsis-v" />
            </DropdownToggle>
            <DropdownMenu right>
              {!isShared && (
                <DropdownItem onClick={onEditClick}>Edit</DropdownItem>
              )}
              <DropdownItem onClick={onGetCodeClick}>
                Template code{' '}
                <i className="fa fa-question-circle" id={'tooltip-' + id} />
                <Tooltip
                  placement="right"
                  className="template-block__tooltip"
                  isOpen={isTooltipOpen}
                  target={'tooltip-' + id}
                  toggle={onTooltipToggle}
                >
                  Copy this Template's code to share with colleagues, clients,
                  or the Customers.ai community.
                </Tooltip>
              </DropdownItem>
              {!isShared && (
                <DropdownItem onClick={onDeleteClick}>Delete</DropdownItem>
              )}
            </DropdownMenu>
          </Dropdown>
        </div>
      )}
      <div className={contentClassName}>
        <div className={titleClassName}>{title}</div>
        {subTitle && (
          <div className="template-block__sub-title">{subTitle}</div>
        )}
        {details && <div className="template-block__details">{details}</div>}
      </div>
    </div>
  );
};

type Props = {
  children: React.ReactNode;
};

const TemplateBlocks = ({ children }: Props) => {
  return <div className="template-blocks">{children}</div>;
};

TemplateBlocks.Item = TemplateBlock;

export { TemplateBlocks };
